import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import PropTypes from 'prop-types'
import Btn from '../../../../../../UI/Btn'
import TextField from '../../../../../../UI/TextField'
import SettingHint from '../SettingHint'
import TemplateLinks from '../TemplateLinks'

import styles from '../style.module.scss'
import Joi from 'joi'
import { joiResolver } from '@hookform/resolvers/joi'
import { useTranslation } from 'react-i18next'

const schema = Joi.object({
	invoicesOverdue: Joi.object().keys({
		value: Joi.number().required()
	})
})

export default function InvoicesOverdue(props) {
	const { t } = useTranslation()
	const {
		register,
		reset,
		handleSubmit,
		formState: { errors }
	} = useForm({
		resolver: joiResolver(schema)
	})

	const onSubmit = form => {
		props
			.updateOptions({ name: 'invoicesOverdue', value: form.invoicesOverdue })
			.then(() => toast.success(t('Options was updated')))
	}

	// Was nach Fristablauf geschieht, hängt an der Nachbareinstellung
	// „Erinnerungen zu Rechnungen". Ohne die eingestellten Zahlen bliebe der
	// Ablauf abstrakt, deshalb stehen sie hier im Text.
	const reminders = props.form?.invoicingReportingReminders
	const firstReminderDays = reminders?.firstReminderDaysInvoiceIssued
	const frequencyDays = reminders?.reminderFrequencyDays

	useEffect(() => {
		reset({ invoicesOverdue: props.form?.invoicesOverdue || {} })
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [props.form])
	return (
		<form id="invoicesOverdue" className={styles.root} onSubmit={handleSubmit(onSubmit)}>
			<section className={styles.section}>
				<h3 className={styles.sectionTitle}>{t('Invoices overdue time')}</h3>
				<SettingHint
					example={t('Example: 30 — an invoice issued on 1 March counts as overdue from 31 March.')}
					warning={t(
						'Takes effect immediately for all existing invoices: lowering the number can mark many invoices as overdue at once.'
					)}
				>
					{t(
						'Number of days after the invoice date from which an invoice counts as overdue. It controls the status shown in the dashboard and in the invoice list, and it starts the payment reminders.'
					)}
				</SettingHint>
				<TemplateLinks
					items={[
						{ channel: 'email', key: 'REMIND_INVOICE' },
						{ channel: 'internal', key: 'INVOICE_PAYMENT_REMINDER' }
					]}
				/>
				<div className={styles.sectionContent}>
					<TextField
						className={styles.field}
						mb={20}
						label={t('days')}
						register={register(`invoicesOverdue.value`)}
						placeholder="Input new overdue time for invoices"
						error={errors.invoicesOverdue?.value}
					/>

					<h4 className={styles.miniSectionTitle}>{t('What happens once the deadline has passed')}</h4>
					<ul className={styles.factList}>
						<li>
							{t(
								'The invoice is listed as "Overdue" in the dashboard, in the invoice list and in the company overview.'
							)}
						</li>
						<li>
							{firstReminderDays
								? t(
										'First payment reminder {{days}} days after the invoice date — but never before the invoice is actually overdue.',
										{ days: firstReminderDays }
								  )
								: t(
										'First payment reminder as configured under "Invoicing Reminders" — but never before the invoice is actually overdue.'
								  )}
						</li>
						<li>
							{frequencyDays
								? t('Repeated every {{days}} days for as long as the invoice remains unpaid.', {
										days: frequencyDays
								  })
								: t('Repeated at the interval configured under "Invoicing Reminders".')}
						</li>
						<li>
							{t(
								'Every student of the company receives the reminder by email; the company and its supervising teacher additionally get a message inside the application.'
							)}
						</li>
						<li>
							{t(
								'Invoices are checked once a night at 3:00 a.m. A reminder therefore goes out in the night after the deadline has passed at the earliest, and each invoice is only reminded once per run.'
							)}
						</li>
					</ul>

					<div className={styles.btnContainer}>
						<Btn
							className={styles.btn}
							type="submit"
							icon="arrow"
							iconSize={{ w: 10, h: 10 }}
							iconColor="#fff"
						>
							{t('save')}
						</Btn>
					</div>
				</div>
			</section>
		</form>
	)
}
InvoicesOverdue.propTypes = {
	form: PropTypes.object.isRequired,
	updateOptions: PropTypes.func.isRequired
}
